import { useEffect, useState } from 'react';
import { Button, Modal, PageState, useToast } from '../../components/ui';
import {
  useApplyTakeover,
  useAvailableCatalogs,
  useConnectCatalog,
  useTakeoverPreview,
  type TakeoverDecision,
  type TakeoverItem,
} from '../../api/whatsapp-catalog';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

/**
 * Pick which of the WhatsApp account's catalogues to connect - with its item
 * count and a Commerce Manager link - instead of silently taking the first.
 */
export function ChooseCatalogModal({
  channelId,
  onClose,
}: {
  channelId: string;
  onClose: () => void;
}) {
  const catalogs = useAvailableCatalogs(channelId, true);
  const connect = useConnectCatalog(channelId);
  const toast = useToast();
  const [connecting, setConnecting] = useState<string | null>(null);

  async function onConnect(id: string) {
    setConnecting(id);
    try {
      const s = await connect.mutateAsync(id);
      toast.success(
        s.counts.failed > 0
          ? `Catalog connected - ${s.counts.failed} product(s) need attention.`
          : 'Catalog connected and synced.',
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not connect the catalog.');
    } finally {
      setConnecting(null);
    }
  }

  return (
    <Modal title="Choose a catalog" onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, ...muted }}>
          These are the Commerce Manager catalogs linked to this WhatsApp account. Your products are
          synced into the one you connect; items already in it are left alone unless you take them
          over.
        </p>
        {catalogs.isPending ? (
          <PageState kind="loading" message="Asking Meta…" />
        ) : catalogs.isError ? (
          <PageState kind="error" message={catalogs.error.message} />
        ) : catalogs.data.length === 0 ? (
          <p style={{ margin: 0 }}>
            No catalog is linked to this WhatsApp account yet. In{' '}
            <a
              href="https://business.facebook.com/wa/manage/home/"
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp Manager
            </a>{' '}
            open Catalog and connect your Commerce Manager catalog (or create one in{' '}
            <a href="https://business.facebook.com/commerce/" target="_blank" rel="noreferrer">
              Commerce Manager
            </a>
            ), then come back here.
          </p>
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {catalogs.data.map((c) => (
              <li
                key={c.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  flexWrap: 'wrap',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-2) var(--space-3)',
                }}
              >
                <span>
                  <strong>{c.name ?? 'Catalog'}</strong>
                  <span style={muted}>
                    {' '}
                    · {c.productCount ?? '?'} item(s) · {c.id} ·{' '}
                    <a href={c.commerceManagerUrl} target="_blank" rel="noreferrer">
                      View in Commerce Manager
                    </a>
                  </span>
                </span>
                {c.connected ? (
                  <span className="ne-badge">Connected</span>
                ) : (
                  <Button
                    type="button"
                    disabled={connect.isPending}
                    onClick={() => void onConnect(c.id)}
                    aria-label={`Connect ${c.name ?? c.id}`}
                  >
                    {connecting === c.id ? 'Connecting…' : 'Connect'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type Choice = { action: 'LINK'; productId: string } | { action: 'IMPORT' } | { action: 'SKIP' };

const initialChoice = (i: TakeoverItem): Choice =>
  i.linkedProductId
    ? { action: 'SKIP' }
    : i.suggestedProductId
      ? { action: 'LINK', productId: i.suggestedProductId }
      : { action: 'IMPORT' };

/**
 * Take over a catalogue that already has items: link each item to one of our
 * products (our details then update it in place - no duplicate) or import it
 * as a new product; matches by id, SKU or exact name are preselected.
 */
export function TakeoverModal({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const preview = useTakeoverPreview(channelId, true);
  const apply = useApplyTakeover(channelId);
  const toast = useToast();
  const [choices, setChoices] = useState<Record<string, Choice>>({});

  useEffect(() => {
    if (preview.data) {
      setChoices(
        Object.fromEntries(preview.data.items.map((i) => [i.retailerId, initialChoice(i)])),
      );
    }
  }, [preview.data]);

  const productList = preview.data?.products ?? [];
  const nameOf = new Map(productList.map((p) => [p.id, p.name]));

  async function onApply() {
    const decisions: TakeoverDecision[] = [];
    for (const item of preview.data?.items ?? []) {
      const c = choices[item.retailerId];
      if (!c || c.action === 'SKIP') continue;
      if (c.action === 'LINK') {
        decisions.push({ retailerId: item.retailerId, action: 'LINK', productId: c.productId });
      } else {
        decisions.push({
          retailerId: item.retailerId,
          action: 'IMPORT',
          name: item.name,
          description: item.description,
          price: item.price,
          currency: item.currency,
        });
      }
    }
    if (decisions.length === 0) {
      toast.error('Choose at least one item to link or import.');
      return;
    }
    try {
      const r = await apply.mutateAsync(decisions);
      toast.success(`${r.linked} linked and ${r.imported} imported - catalog synced.`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not take over the catalog items.');
    }
  }

  const set = (retailerId: string, value: string) =>
    setChoices((c) => ({
      ...c,
      [retailerId]:
        value === 'IMPORT'
          ? { action: 'IMPORT' }
          : value === 'SKIP'
            ? { action: 'SKIP' }
            : { action: 'LINK', productId: value },
    }));

  return (
    <Modal title="Take over existing catalog items" onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, ...muted }}>
          Link an item to one of your products, or import it as a new product. Linked items are then
          kept up to date from your products (name, price, photo, description), and WhatsApp carts
          with them become orders for that product.
        </p>
        {preview.isPending ? (
          <PageState kind="loading" message="Reading the catalog…" />
        ) : preview.isError ? (
          <PageState kind="error" message={preview.error.message} />
        ) : preview.data.items.length === 0 ? (
          <PageState kind="empty" title="This catalog has no items yet" />
        ) : (
          <>
            {preview.data.truncated ? (
              <p style={{ margin: 0, ...muted }}>Showing the first 2,000 items.</p>
            ) : null}
            <div style={{ overflowX: 'auto', maxHeight: '55vh' }}>
              <table className="ne-table" style={{ minWidth: 620 }}>
                <thead>
                  <tr>
                    <th>Item in the catalog</th>
                    <th style={{ textAlign: 'right' }}>Price</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.items.map((i) => {
                    const c = choices[i.retailerId] ?? initialChoice(i);
                    const value = c.action === 'LINK' ? c.productId : c.action;
                    return (
                      <tr key={i.retailerId}>
                        <td>
                          <div
                            style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}
                          >
                            {i.imageUrl ? (
                              <img
                                src={i.imageUrl}
                                alt=""
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: 'cover',
                                  borderRadius: 4,
                                }}
                              />
                            ) : null}
                            <div>
                              <strong>{i.name}</strong>
                              <div style={muted}>{i.retailerId}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {i.price !== null ? `${i.currency ?? ''} ${i.price.toFixed(2)}` : '—'}
                        </td>
                        <td>
                          {i.linkedProductId ? (
                            <span className="ne-badge">
                              Linked to {nameOf.get(i.linkedProductId) ?? 'a product'}
                            </span>
                          ) : (
                            <select
                              className="ne-input"
                              aria-label={`Action for ${i.name}`}
                              value={value}
                              onChange={(e) => set(i.retailerId, e.target.value)}
                            >
                              <option value="IMPORT">Import as new product</option>
                              <option value="SKIP">Skip</option>
                              <optgroup label="Link to product">
                                {productList.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                    {p.id === i.suggestedProductId ? ' (looks the same)' : ''}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={apply.isPending || !preview.data?.items.length}
            onClick={() => void onApply()}
          >
            {apply.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
